import { useEffect, useRef, useState } from "react";
import { useSnapshot } from "valtio";
import { updateAccessCards } from "../../../apis/wedding/update.access.cards";
import DatePicker from "../../../components/DatePicker";
import TailwindColorPicker from "../../../components/TailwindColorPicker";
import TWButton from "../../../components/TWButton";
import useImageUpload from "../../../hooks/useImageUpload";
import { accessCardState } from "../../../stores/accessCardStore";
import { MediaAssetClient } from "../../../types/media.asset";
import { AccessCards, QRPosition } from "../../../types/wedding";
import { formatDateAndTime, getAccessCardDate } from "../../../utils/dateUtils";
import { editorState } from "../../../utils/editor";
import { getEventTypeFromEditor } from "../../../utils/guest";
import { ubxdidUpdateDebounceToken } from "../../Website/utils";
import EventSelectionDesignOverlay from "./components/EventSelectionDesignOverlay";

const DEBOUNCE_DELAY = 1000;
const ACCESS_CARD_DRAG_LAYOUT_V2_ENABLED = import.meta.env.VITE_ACCESS_CARD_DRAG_LAYOUT_V2 === "true";
type EditorPanel = "content" | "background" | "text" | "qr" | null;

const QR_POSITION_ANCHORS: { position: QRPosition; label: string; className: string }[] = [
  { position: "top-left", label: "Top left", className: "left-2 top-2" },
  { position: "top-center", label: "Top center", className: "left-1/2 top-2 -translate-x-1/2" },
  { position: "top-right", label: "Top right", className: "right-2 top-2" },
  { position: "center", label: "Center", className: "left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2" },
  { position: "bottom-left", label: "Bottom left", className: "bottom-2 left-2" },
  { position: "bottom-center", label: "Bottom center", className: "bottom-2 left-1/2 -translate-x-1/2" },
  { position: "bottom-right", label: "Bottom right", className: "bottom-2 right-2" },
];

const isMeaningfulDate = (value: Date | string | undefined) => {
  const date = value ? new Date(value) : null;
  return Boolean(date && !Number.isNaN(date.getTime()) && date.getFullYear() >= 2000);
};

const parseHex = (value?: string) => {
  const hex = value?.replace("#", "");
  if (!hex || !/^[0-9a-f]{3}([0-9a-f]{3})?$/i.test(hex)) return null;
  const normalized = hex.length === 3 ? hex.split("").map((part) => part + part).join("") : hex;
  return [0, 2, 4].map((index) => parseInt(normalized.slice(index, index + 2), 16));
};

const luminance = (rgb: number[]) => {
  const [r, g, b] = rgb.map((channel) => {
    const value = channel / 255;
    return value <= 0.04045 ? value / 12.92 : ((value + 0.055) / 1.055) ** 2.4;
  });
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
};

const getContrastRecommendation = (accessCards: AccessCards) => {
  const text = parseHex(accessCards.textColor?.colorCode);
  let background = parseHex(accessCards.backgroundColor?.colorCode) || [255, 255, 255];
  if (accessCards.backgroundCtx === "image") {
    const overlay = parseHex(accessCards.overlayColor?.colorCode) || [0, 0, 0];
    const opacity = accessCards.overlayOpacity ?? 0.7;
    background = overlay.map((channel) => channel * opacity + 128 * (1 - opacity));
  }
  if (!text) return null;
  const foreground = luminance(text);
  const canvas = luminance(background);
  const ratio = (Math.max(foreground, canvas) + 0.05) / (Math.min(foreground, canvas) + 0.05);
  if (ratio >= 4.5) return null;
  const useWhite = 1.05 / (canvas + 0.05) >= (canvas + 0.05) / 0.05;
  return useWhite
    ? { colorName: "White", colorCode: "#FFFFFF" }
    : { colorName: "Black", colorCode: "#000000" };
};

const ComponentCard = ({
  title,
  summary,
  detail,
  swatch,
  onClick,
}: {
  title: string;
  summary: string;
  detail?: string;
  swatch?: string;
  onClick: () => void;
}) => (
  <button
    type="button"
    onClick={onClick}
    className="group w-full rounded-2xl border border-[#E6E7E3] bg-white p-4 text-left transition hover:border-[#AEB6AF] hover:shadow-sm focus:outline-none focus:ring-2 focus:ring-[#172C20]/20"
  >
    <div className="flex items-start justify-between gap-3">
      <div className="min-w-0">
        <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-[#172C20]">{title}</p>
        <div className="mt-2 flex items-center gap-2">
          {swatch && <span className="h-5 w-5 shrink-0 rounded-full border border-black/10" style={{ backgroundColor: swatch }} />}
          <p className="truncate text-sm font-medium text-gray-900">{summary}</p>
        </div>
        {detail && <p className="mt-1 truncate text-xs text-gray-500">{detail}</p>}
      </div>
      <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[#F3F4F1] text-gray-600 transition group-hover:bg-[#172C20] group-hover:text-white" aria-hidden="true">
        <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="1.8">
          <path d="m9 18 6-6-6-6" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </span>
    </div>
  </button>
);

const EditorSheet = ({ title, onClose, children }: { title: string; onClose: () => void; children: React.ReactNode }) => (
  <>
    <button type="button" aria-label="Close editor" onClick={onClose} className="fixed inset-0 z-40 bg-black/35 md:hidden" />
    <section className="fixed inset-x-0 bottom-0 z-50 max-h-[85vh] overflow-y-auto rounded-t-3xl bg-white shadow-2xl md:absolute md:inset-0 md:max-h-none md:rounded-none md:shadow-none">
      <header className="sticky top-0 z-10 flex items-center justify-between border-b border-gray-200 bg-white px-5 py-4">
        <div>
          <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-gray-400">Customise</p>
          <h2 className="mt-1 text-base font-semibold text-gray-900">{title}</h2>
        </div>
        <button type="button" onClick={onClose} aria-label="Close" className="flex h-9 w-9 items-center justify-center rounded-full bg-gray-100 text-xl text-gray-600">×</button>
      </header>
      <div className="space-y-6 px-5 py-5 pb-[calc(24px+env(safe-area-inset-bottom))]">{children}</div>
    </section>
  </>
);

const ToggleRow = ({ label, checked, onChange }: { label: string; checked: boolean; onChange: (checked: boolean) => void }) => (
  <label className="flex cursor-pointer items-center justify-between border-b border-gray-100 py-3 last:border-0">
    <span className="text-sm text-gray-700">{label}</span>
    <input type="checkbox" checked={checked} onChange={(event) => onChange(event.target.checked)} className="h-4 w-4 rounded border-gray-300 accent-[#172C20]" />
  </label>
);

const QRPositionPicker = ({ value, onChange }: { value: QRPosition; onChange: (position: QRPosition) => void }) => (
  <div
    className="relative mx-auto aspect-[4/5] h-44 rounded-xl border border-gray-300 bg-[#F7F8F5] shadow-inner"
    data-drag-layout-v2={ACCESS_CARD_DRAG_LAYOUT_V2_ENABLED ? "enabled" : "disabled"}
  >
    <div className="pointer-events-none absolute inset-x-4 top-1/3 border-t border-dashed border-gray-200" />
    <div className="pointer-events-none absolute inset-x-4 top-2/3 border-t border-dashed border-gray-200" />
    <div className="pointer-events-none absolute inset-y-4 left-1/2 border-l border-dashed border-gray-200" />
    {QR_POSITION_ANCHORS.map(({ position, label, className }) => {
      const selected = value === position;
      return (
        <button
          key={position}
          type="button"
          aria-label={`Place QR code at ${label.toLowerCase()}`}
          aria-pressed={selected}
          title={label}
          onClick={() => onChange(position)}
          className={`absolute flex h-8 w-8 items-center justify-center rounded-lg border-2 transition ${className} ${selected ? "border-[#172C20] bg-[#172C20] text-white shadow-md" : "border-gray-300 bg-white text-gray-500 hover:border-[#315F48] hover:text-[#315F48]"}`}
        >
          <span className="grid h-4 w-4 grid-cols-2 gap-[2px]" aria-hidden="true">
            <span className="border border-current" /><span className="border border-current" />
            <span className="border border-current" /><span className="border border-current" />
          </span>
        </button>
      );
    })}
  </div>
);

const QRDesignPage = () => {
  const { accessCards, mainContent, XAccountHash, eventSchedules } = useSnapshot(editorState);
  const { selectedEvent, selectedEventType } = useSnapshot(accessCardState);
  const [activePanel, setActivePanel] = useState<EditorPanel>(null);
  const [showEventSelection, setShowEventSelection] = useState(() => !selectedEvent);
  const [saveStatus, setSaveStatus] = useState<"idle" | "saving" | "saved" | "error">("idle");
  const [lastSaved, setLastSaved] = useState<string | null>(null);
  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const qrResizeFrameRef = useRef<number | null>(null);

  const currDate = getAccessCardDate(accessCards?.date, mainContent?.date);
  const selectedSchedule = eventSchedules?.find((schedule) => schedule._id === selectedEvent);
  const contrastRecommendation = getContrastRecommendation(accessCards as unknown as AccessCards);
  const isDateValid = isMeaningfulDate(accessCards?.date);
  const backgroundSummary = accessCards.backgroundCtx === "image"
    ? accessCards.backgroundImage ? "Background image" : "Image not selected"
    : accessCards.backgroundColor?.colorName || "Solid colour";
  const visibleDetails = [
    accessCards.showEventName && "Event name",
    accessCards.showGuestName && "Guest name",
    accessCards.showAdmitsOneOnly && "Admits-one badge",
  ].filter(Boolean).join(" · ") || "Essential details only";

  const { uploading, uploadImage } = useImageUpload({
    field: "accessCards.backgroundImage",
    collectionID: XAccountHash || "",
    collection: "wedding",
    mediaAssetClient: MediaAssetClient.MediaAssetClientAWSS3,
  });

  const handleSave = (_accessCards: AccessCards) => {
    const token = Math.random();
    ubxdidUpdateDebounceToken.value = token;
    setSaveStatus("saving");
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(() => {
      if (ubxdidUpdateDebounceToken.value !== token) return;
      const date = isMeaningfulDate(_accessCards.date)
        ? new Date(_accessCards.date)
        : selectedSchedule?.date ? new Date(selectedSchedule.date) : null;
      if (!date) {
        setSaveStatus("error");
        return;
      }
      updateAccessCards({
        headers: { "X-Account-Hash": XAccountHash },
        body: {
          accessCards: {
            date: date.toISOString() as any,
            backgroundCtx: _accessCards.backgroundCtx,
            backgroundColor: _accessCards.backgroundColor,
            textColor: _accessCards.textColor,
            backgroundImage: _accessCards.backgroundImage,
            hideDetails: _accessCards.hideDetails,
            address: _accessCards.address,
            showGuestName: _accessCards.showGuestName,
            showAdmitsOneOnly: _accessCards.showAdmitsOneOnly,
            showEventName: _accessCards.showEventName,
            eventId: _accessCards.eventId,
            selectedEventType: _accessCards.selectedEventType,
            overlayColor: _accessCards.overlayColor,
            overlayOpacity: _accessCards.overlayOpacity,
            qrPosition: _accessCards.qrPosition,
            qrCustomPosition: _accessCards.qrCustomPosition,
            qrSize: _accessCards.qrSize,
          },
        },
        onCompleted() {
          setSaveStatus("saved");
          setLastSaved(formatDateAndTime(new Date()));
        },
        onError(error) {
          console.error("Failed to update access cards:", error);
          setSaveStatus("error");
        },
      });
    }, DEBOUNCE_DELAY);
  };

  useEffect(() => () => {
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    if (qrResizeFrameRef.current) cancelAnimationFrame(qrResizeFrameRef.current);
  }, []);

  const previewQrSize = (value: number) => {
    if (qrResizeFrameRef.current) cancelAnimationFrame(qrResizeFrameRef.current);
    qrResizeFrameRef.current = requestAnimationFrame(() => {
      editorState.accessCards.qrSize = value;
      qrResizeFrameRef.current = null;
    });
  };

  useEffect(() => {
    if (selectedEvent) {
      setShowEventSelection(false);
      return;
    }
    if (accessCards?.eventId && eventSchedules?.length) {
      const schedule = eventSchedules.find((item) => item._id === accessCards.eventId);
      if (schedule) {
        accessCardState.setSelectedEventWithDetails(schedule._id, schedule.eventType || "");
        return;
      }
    }
    setShowEventSelection(true);
  }, [accessCards?.eventId, eventSchedules, selectedEvent]);

  useEffect(() => {
    if (!selectedEvent || !selectedSchedule) return;
    const needsDateRepair = !isMeaningfulDate(accessCards?.date);
    const needsEventRepair = accessCards.eventId !== selectedEvent;
    if (!needsDateRepair && !needsEventRepair) return;
    editorState.accessCards = {
      ...editorState.accessCards,
      eventId: selectedEvent,
      selectedEventType: selectedSchedule.eventType || selectedEventType || "",
      ...(needsDateRepair ? { date: new Date(selectedSchedule.date) } : {}),
      ...(!editorState.accessCards.address && selectedSchedule.address ? { address: selectedSchedule.address } : {}),
    };
    handleSave(editorState.accessCards);
  }, [accessCards?.date, accessCards?.eventId, selectedEvent, selectedEventType, selectedSchedule]);

  useEffect(() => {
    if (accessCards.qrPosition !== "middle-left" && accessCards.qrPosition !== "middle-right") return;
    editorState.accessCards.qrPosition = "center";
    handleSave(editorState.accessCards);
  }, [accessCards.qrPosition]);

  const handleEventSelected = (eventId: string, eventType: string) => {
    const event = editorState.eventSchedules?.find((schedule) => schedule._id === eventId);
    editorState.accessCards = {
      ...editorState.accessCards,
      eventId,
      selectedEventType: eventType,
      ...(event?.date ? { date: new Date(event.date) } : {}),
      address: event?.address || "",
    };
    accessCardState.setSelectedEventWithDetails(eventId, eventType);
    handleSave(editorState.accessCards);
  };

  const updateVisibility = (field: "showGuestName" | "showAdmitsOneOnly" | "showEventName", checked: boolean) => {
    editorState.accessCards[field] = checked;
    handleSave(editorState.accessCards);
  };

  if (showEventSelection) return <EventSelectionDesignOverlay onEventSelected={handleEventSelected} />;

  return (
    <div className="relative mx-auto min-h-full w-full px-1 pb-28 md:w-[70%] md:pb-8">
      <div className="mb-4 flex items-start justify-between px-1">
        <div>
          <h1 className="text-sm font-semibold uppercase tracking-[0.12em] text-[#172C20]">Customise access card</h1>
          <p className="mt-1 text-xs text-gray-500">Choose the content, then style each part.</p>
        </div>
        <span className={`text-[11px] ${saveStatus === "error" ? "text-red-600" : "text-gray-400"}`}>
          {saveStatus === "saving" ? "Saving…" : saveStatus === "error" ? "Save failed" : lastSaved ? `Saved ${lastSaved}` : "Autosaves"}
        </span>
      </div>

      {!isDateValid && (
        <button type="button" onClick={() => setActivePanel("content")} className="mb-3 w-full rounded-xl border border-amber-300 bg-amber-50 p-3 text-left text-xs leading-5 text-amber-900">
          This event has an invalid date. Review the event details before generating cards.
        </button>
      )}

      <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
        <ComponentCard
          title="Event & content"
          summary={selectedEventType || "Choose an event"}
          detail={`${isDateValid ? new Date(currDate).toLocaleDateString() : "Date not set"} · ${accessCards.address || "Venue not set"} · ${visibleDetails}`}
          onClick={() => setActivePanel("content")}
        />
        <ComponentCard
          title="Card background"
          summary={backgroundSummary}
          detail={accessCards.backgroundCtx === "image" ? `${accessCards.overlayColor?.colorName || "Black"} overlay · ${Math.round((accessCards.overlayOpacity ?? 0.7) * 100)}%` : "Applied to the whole card"}
          swatch={accessCards.backgroundCtx === "image" ? accessCards.overlayColor?.colorCode || "#000000" : accessCards.backgroundColor?.colorCode || "#FFFFFF"}
          onClick={() => setActivePanel("background")}
        />
        <ComponentCard
          title="Card text"
          summary={accessCards.textColor?.colorName || "Text colour"}
          detail={contrastRecommendation ? "Contrast needs attention" : "Readable against the current background"}
          swatch={accessCards.textColor?.colorCode || "#000000"}
          onClick={() => setActivePanel("text")}
        />
        <ComponentCard
          title="QR code"
          summary={`${(accessCards.qrPosition || "center").replace("-", " ")} · ${accessCards.qrSize || 100}px`}
          detail="Position and scale"
          onClick={() => setActivePanel("qr")}
        />
      </div>

      <div className="mt-5 flex items-center justify-between px-1">
        <button type="button" onClick={() => setShowEventSelection(true)} className="text-xs font-medium text-[#315F48] underline underline-offset-4">Change event</button>
      </div>

      <div className="fixed inset-x-0 bottom-0 z-30 border-t border-gray-200 bg-white/95 px-4 pb-[calc(12px+env(safe-area-inset-bottom))] pt-3 backdrop-blur md:hidden">
        <TWButton variant="primary" size="lg" onClick={() => accessCardState.setPreviewOpen(true)}>PREVIEW ACCESS CARD</TWButton>
      </div>

      {activePanel === "content" && (
        <EditorSheet title="Event & content" onClose={() => setActivePanel(null)}>
          <div className="rounded-xl bg-[#F7F8F5] p-4">
            <div className="flex items-start justify-between gap-3">
              <div><p className="text-xs uppercase tracking-wide text-gray-500">Selected event</p><p className="mt-1 text-sm font-medium">{selectedEventType}</p></div>
              <button type="button" onClick={() => { setActivePanel(null); setShowEventSelection(true); }} className="text-xs font-medium text-[#315F48]">Change</button>
            </div>
          </div>
          <div>
            <label className="mb-2 block text-xs font-medium uppercase tracking-wide text-gray-600">Date & time</label>
            <DatePicker value={currDate} onChange={(date) => { editorState.accessCards.date = new Date(date); handleSave(editorState.accessCards); }} placeholder="Select event date & time" includeTime />
          </div>
          <div>
            <label htmlFor="venue-input" className="mb-2 block text-xs font-medium uppercase tracking-wide text-gray-600">Venue</label>
            <input id="venue-input" type="text" value={accessCards.address || ""} onChange={(event) => { editorState.accessCards.address = event.target.value; }} onBlur={() => handleSave(editorState.accessCards)} className="w-full rounded-xl border border-gray-200 px-3 py-3 text-sm outline-none focus:border-[#315F48]" placeholder="Enter venue" />
          </div>
          <div className="rounded-xl border border-gray-200 px-4">
            <ToggleRow label="Event name" checked={accessCards.showEventName ?? true} onChange={(value) => updateVisibility("showEventName", value)} />
            <ToggleRow label="Guest name" checked={accessCards.showGuestName ?? false} onChange={(value) => updateVisibility("showGuestName", value)} />
            <ToggleRow label="Admits-one badge" checked={accessCards.showAdmitsOneOnly ?? false} onChange={(value) => updateVisibility("showAdmitsOneOnly", value)} />
          </div>
          <TWButton variant="primary" size="lg" onClick={() => setActivePanel(null)}>DONE</TWButton>
        </EditorSheet>
      )}

      {activePanel === "background" && (
        <EditorSheet title="Card background" onClose={() => setActivePanel(null)}>
          <div className="grid grid-cols-2 gap-2 rounded-xl bg-gray-100 p-1">
            {(["color", "image"] as const).map((type) => (
              <button key={type} type="button" onClick={() => { editorState.accessCards.backgroundCtx = type; handleSave(editorState.accessCards); }} className={`rounded-lg py-2.5 text-xs font-medium uppercase ${accessCards.backgroundCtx === type ? "bg-white text-gray-900 shadow-sm" : "text-gray-500"}`}>{type}</button>
            ))}
          </div>
          {accessCards.backgroundCtx === "color" ? (
            <TailwindColorPicker id="background-color-input" key={`background-${accessCards.backgroundColor?.colorCode}`} colorCode={accessCards.backgroundColor?.colorCode || "#FFFFFF"} colorName={accessCards.backgroundColor?.colorName || "White"} onColorCodeChange={(colorCode) => { editorState.accessCards.backgroundColor.colorCode = colorCode; handleSave(editorState.accessCards); }} onColorNameChange={(colorName) => { editorState.accessCards.backgroundColor.colorName = colorName; handleSave(editorState.accessCards); }} placeholder="Background colour" />
          ) : (
            <>
              {accessCards.backgroundImage ? (
                <div className="overflow-hidden rounded-xl border border-gray-200"><img src={accessCards.backgroundImage} alt="Current access card background" className="h-36 w-full object-cover" /><div className="flex items-center justify-between px-3 py-3"><span className="text-xs text-gray-500">Current background</span><button type="button" onClick={() => { editorState.accessCards.backgroundImage = ""; handleSave(editorState.accessCards); }} className="text-xs font-medium text-red-600">Remove</button></div></div>
              ) : <div className="rounded-xl border border-dashed border-gray-300 p-6 text-center text-xs text-gray-500">No background image selected</div>}
              <label className="flex cursor-pointer items-center justify-center rounded-full bg-gray-100 px-4 py-3 text-xs font-medium uppercase text-gray-700 hover:bg-gray-200">
                {uploading ? "Uploading…" : accessCards.backgroundImage ? "Replace image" : "Choose image"}
                <input type="file" accept="image/*" disabled={uploading} className="sr-only" onChange={(event) => { const file = event.target.files?.[0]; if (file) uploadImage(file, (imageUrl) => { editorState.accessCards.backgroundImage = imageUrl; handleSave(editorState.accessCards); }); }} />
              </label>
              <div><label className="mb-2 block text-xs font-medium uppercase tracking-wide text-gray-600">Overlay colour</label><TailwindColorPicker id="overlay-color-input" key={`overlay-${accessCards.overlayColor?.colorCode}`} colorCode={accessCards.overlayColor?.colorCode || "#000000"} colorName={accessCards.overlayColor?.colorName || "Black"} onColorCodeChange={(colorCode) => { editorState.accessCards.overlayColor = { colorName: editorState.accessCards.overlayColor?.colorName || "Custom", colorCode }; handleSave(editorState.accessCards); }} onColorNameChange={(colorName) => { editorState.accessCards.overlayColor = { colorName, colorCode: editorState.accessCards.overlayColor?.colorCode || "#000000" }; handleSave(editorState.accessCards); }} placeholder="Overlay colour" /></div>
              <div><div className="mb-2 flex justify-between"><label htmlFor="overlay-opacity" className="text-xs font-medium uppercase tracking-wide text-gray-600">Overlay opacity</label><span className="text-xs text-gray-500">{Math.round((accessCards.overlayOpacity ?? 0.7) * 100)}%</span></div><input id="overlay-opacity" type="range" min="0" max="100" step="5" value={(accessCards.overlayOpacity ?? 0.7) * 100} onChange={(event) => { editorState.accessCards.overlayOpacity = Number(event.target.value) / 100; handleSave(editorState.accessCards); }} className="w-full accent-[#172C20]" /></div>
            </>
          )}
          <TWButton variant="primary" size="lg" onClick={() => setActivePanel(null)}>DONE</TWButton>
        </EditorSheet>
      )}

      {activePanel === "text" && (
        <EditorSheet title="Card text" onClose={() => setActivePanel(null)}>
          <p className="text-xs leading-5 text-gray-500">This colour is applied to all text and preview icons on the card.</p>
          <TailwindColorPicker id="text-color-input" key={`text-${accessCards.textColor?.colorCode}`} colorCode={accessCards.textColor?.colorCode || "#000000"} colorName={accessCards.textColor?.colorName || "Black"} onColorCodeChange={(colorCode) => { editorState.accessCards.textColor.colorCode = colorCode; handleSave(editorState.accessCards); }} onColorNameChange={(colorName) => { editorState.accessCards.textColor.colorName = colorName; handleSave(editorState.accessCards); }} placeholder="Text colour" />
          {contrastRecommendation ? <div className="rounded-xl border border-amber-300 bg-amber-50 p-4"><p className="text-xs leading-5 text-amber-900">This colour does not have enough contrast against the current background.</p><button type="button" className="mt-3 rounded-full bg-[#172C20] px-4 py-2 text-xs font-medium uppercase text-white" onClick={() => { editorState.accessCards.textColor = contrastRecommendation; handleSave(editorState.accessCards); }}>Use {contrastRecommendation.colorName}</button></div> : <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4 text-xs text-emerald-800">Good contrast for readable card text.</div>}
          <TWButton variant="primary" size="lg" onClick={() => setActivePanel(null)}>DONE</TWButton>
        </EditorSheet>
      )}

      {activePanel === "qr" && (
        <EditorSheet title="QR code" onClose={() => setActivePanel(null)}>
          <div><div className="mb-3"><p className="text-xs font-medium uppercase tracking-wide text-gray-600">Position</p><p className="mt-1 text-xs text-gray-500">Choose an anchor point on the card.</p></div><QRPositionPicker value={(accessCards.qrPosition || "center") as QRPosition} onChange={(position) => { editorState.accessCards.qrPosition = position; handleSave(editorState.accessCards); }} /></div>
          <div><div className="mb-2 flex justify-between"><label htmlFor="qr-size" className="text-xs font-medium uppercase tracking-wide text-gray-600">Size</label><span className="text-xs tabular-nums text-gray-500">{accessCards.qrSize || 100}px</span></div><input id="qr-size" type="range" min="60" max="200" step="5" value={accessCards.qrSize || 100} onInput={(event) => previewQrSize(Number(event.currentTarget.value))} onChange={(event) => { const size = Number(event.currentTarget.value); editorState.accessCards.qrSize = size; handleSave(editorState.accessCards); }} onPointerUp={() => handleSave(editorState.accessCards)} onKeyUp={() => handleSave(editorState.accessCards)} className="w-full accent-[#172C20]" /></div>
          <button type="button" onClick={() => { editorState.accessCards.qrPosition = "center"; editorState.accessCards.qrSize = 100; handleSave(editorState.accessCards); }} className="text-left text-xs font-medium text-[#315F48] underline underline-offset-4">Reset QR layout</button>
          <TWButton variant="primary" size="lg" onClick={() => setActivePanel(null)}>DONE</TWButton>
        </EditorSheet>
      )}
    </div>
  );
};

export default QRDesignPage;
